// noinspection JSUnusedGlobalSymbols

import _ from 'lodash';
import crossfilter from 'crossfilter2';
import {toast, type ToastOptions} from "react-toastify";
import {abbreviateNumber} from 'js-abbreviation-number';
import Moment from "moment";

import {config} from '@isudio/config';
import {$store} from '@isudio/globals';
import type {IUser,  IUserSession} from "@isudio/data";
import {AccountTypes, UserStatus} from "@isudio/data";
import {ToastIcons} from "@isudio/ui";
import {Strings} from '../types/exports';

const debouncedToastError = _.debounce((message: string, options = {autoClose: 3000}, id?: string): void => {
  toast.error(message, {
    ...options,
    containerId: id ?? undefined,
    icon: options?.icon ?? ToastIcons.error,
    style: {
      "background": "#660a0a",
      "color": "#ffc9c9"
    }
  });
}, 3000, {
  leading: true,
  trailing: false
});

const debouncedToastInfo = _.debounce((message: string, options = {autoClose: 3000}, id?: string): void => {
  toast.info(message, {
    ...options,
    containerId: id ?? undefined,
    icon: options?.icon ?? ToastIcons.info,
    style: {
      "background": "181818F2",
      "color": "#ffffff"
    }
  });
}, 3000, {
  leading: true,
  trailing: false
});

_.mixin({
  abbreviate(num: number, decimal?: number): string {
    return abbreviateNumber(num, decimal);
  }
});

_.mixin({
  base64ToBlob(base64: string, slice = false, sliceSize: number = 512): Blob | null {
    if (base64.length < 1) return null;
    const arr = base64.split(",");
    const byteString = atob(arr.pop() || base64);
    const mimeMatch = arr[0]?.match(/:(.*?);/);
    const mimeType = mimeMatch ? mimeMatch[1] : "application/octet-stream";

    if (!slice) {
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new Blob([ab], { type: mimeType });

    } else {
      let byteArrays = [];

      for (let offset = 0; offset < byteString.length; offset += sliceSize) {
        let slice = byteString.slice(offset, offset + sliceSize);

        let byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        let byteArray = new Uint8Array(byteNumbers);

        byteArrays.push(byteArray);
      }
      return new Blob(byteArrays, {type: mimeType});
    }
  }
});

_.mixin({
  async blobFromObjectUrl(objectUrl: string): Promise<any> {
    return await fetch(objectUrl).then(async r => {
      const blob = (r.ok ? await r.blob() : null);
      return Promise.resolve(blob);

    }).catch(() => {
      return Promise.resolve(null);
    });
  }
});

_.mixin({
  blobToDataURI(blob) {
    try {
      let f = new FileReader();
      f.onload = (e: any) => {
        return e.target['result'];
      };
      return f.readAsDataURL(blob);

    } catch (err) {
      return err;
    }
  }
});

_.mixin({
  cdn(o: any): string {
    const cdn = _.get($store, 'Data.server$.value.data.server.cdn', 'local');
    const isLocalCdn = (cdn === 'local');
    const path = _.get($store, 'App.state$.value.window.size');
    return _.get(o, `${cdn}` + (isLocalCdn && _.isObject(_.get(o, 'local')) ? `.${path}` : ''),
      _.get(o, `${cdn}` + (isLocalCdn && _.isObject(_.get(o, 'local')) ? `.default.${path}` : '')));
  }
});

_.mixin({
  createObjectURL(blob: any): string {
    return URL.createObjectURL(blob);
  }
});

_.mixin({
  crossFilter(dataset) {
    return crossfilter(dataset);
  }
});

_.mixin({
  dataURItoBlob(dataURI): any {
    let byteString = atob(dataURI.split(',')[1]);
    let mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    let arrayBuffer = new ArrayBuffer(byteString.length);
    let _ia = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      _ia[i] = byteString.charCodeAt(i);
    }

    let dataView = new DataView(arrayBuffer);
    return new Blob([dataView], {type: mimeString});
  }
});

_.mixin({
  getCSSProperty(props: any): any {
    let style: CSSStyleDeclaration = document.documentElement.style;
    for (let i: number = 0; i < props.length; i++) {
      if (props[i] in style) {
        return props[i];
      }
    }
    return false;
  }
});

_.mixin({
  getCSSTransitionProperty(): any {
    return _.getCSSProperty(['webkitTransition', 'transition', 'OTransition', 'MozTransition', 'msTransition']);
  }
});

_.mixin({
  getElement(id: string): HTMLElement {
    const c: any = document.getElementById(id);
    if (_.get(c, 'length', 0) > 0) {
      return c[0] as HTMLElement;
    } else {
      const c = document.getElementsByClassName(id);
      return (_.get(c, 'length', 0) > 0 ? c[0] : c) as HTMLElement;
    }
  }
});

_.mixin({
  getPolicyDate(): string {
    const today = Moment();
    const currentYear = today.year();

    // 1. Define June 8th of the current year
    const juneEighth = Moment([currentYear, 5, 8]); // Month is 0-indexed (5 = June)

    // 2. If current date is after June 8th, always return June 8th
    if (today.isAfter(juneEighth, 'day')) {
      return juneEighth.format("D-MMM-YYYY");
    }

    // 3. If before June 8th, check against the 8th of the current month
    const eighthOfCurrentMonth = Moment().date(8);

    if (today.isBefore(eighthOfCurrentMonth, 'day')) {
      // If today is before the 8th of this month, return 8th of previous month
      return eighthOfCurrentMonth.subtract(1, 'month').format("D-MMM-YYYY");
    } else {
      // If today is on or after the 8th of this month, return 8th of current month
      return eighthOfCurrentMonth.format("D-MMM-YYYY");
    }
  }
});

_.mixin({
  getSession(): IUserSession | null {
    return _.get($store, 'User.session$.value', null);
  }
});

_.mixin({
  getUser(): IUser | null {
    return _.get($store, 'User.user$.value', null);
  }
});

_.mixin({
  isAndroid(): boolean {
    return matchMedia('(pointer: coarse)').matches && /Android/i.test(navigator.userAgent);
  }
});

_.mixin({
  isAnimationsEnabled(): boolean {
    return !(matchMedia('(prefers-reduced-motion)').matches);
  }
});

_.mixin({
  isBlob(o: any): boolean {
    switch (typeof o) {
      case 'object':
        if (_.has(o, 'mimeType')) {
          const cdn = _.get(o, 'cdn');
          return _.startsWith(cdn, 'blob:');
        }
        return ((!_.isNothing(_.get(o, 'type', null))) && (_.get(o, 'size', _.get(o, 'blob.size')) > 0));

      case 'string':
        return _.startsWith(o, 'blob:');
    }
    return false;
  }
});

_.mixin({
  isBlobbed(o: any): boolean {
    switch (typeof o) {
      case 'object':
        if (_.isArray(o)) {
          return (_.get(_.filter(o, m => !_.isBlob(m)), 'length', 0) === 0);
        }
        return false;
      case 'string':
        return _.isBlob(o);
      default:
        return false;
    }
  }
});

_.mixin({
  isDebug(): boolean {
    return config.app.debug;
  }
});

_.mixin({
  isDark(): boolean {
    const isClient = typeof window !== 'undefined';
    return isClient ? window.matchMedia("(prefers-color-scheme: dark)").matches : false;
  }
});

_.mixin({
  isIOS(): boolean {
    return matchMedia('(pointer: coarse)').matches && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
});

_.mixin({
  isLandscape(): boolean {
    return _.get($store, 'App.state$.value.window.orientation') === 'landscape';
  }
});

_.mixin({
  isLargeDevice(): boolean {
    return (_.indexOf(['size-xs', 'size-sm'], _.get($store, 'App.state$.value.window.size')) < 0);
  }
});

_.mixin({
  isPortrait(): boolean {
    return _.get($store, 'App.state$.value.window.orientation') === 'portrait';
  }
});

_.mixin({
  isUserActivationPending(): boolean {
    return _.get($store, 'User.user$.value.status', null) === UserStatus.activationPending;
  }
});

_.mixin({
  isUserActive(): boolean {
    return _.get($store, 'User.user$.value.status', null) === UserStatus.active;
  }
});

_.mixin({
  isUserAdmin(): boolean {
    return _.get($store, 'User.user$.value.accountType', null) === AccountTypes.admin;
  }
});

_.mixin({
  isUserAvailable(): boolean {
    return !_.isNothing(_.get($store, 'User.user$.value', null));
  }
});

_.mixin({
  isUserBizAdmin(): boolean {
    return _.get($store, 'User.user$.value.accountType', null) === AccountTypes.bizAdmin;
  }
});

_.mixin({
  isUserSignedIn(): boolean {
    return _.get($store, 'User.signedIn$.value', false);
  }
});

_.mixin({
  isUserStudent(): boolean {
    return _.get($store, 'User.user$.value.accountType', null) === AccountTypes.student;
  }
});

_.mixin({
  async isValidObjectUrl(objectUrl: string): Promise<boolean> {
    return await fetch(objectUrl).then(blob => {
      return Promise.resolve(blob.ok);
    }).catch(() => {
      return Promise.resolve(false);
    });
  }
});

_.mixin({
  isValidSession(): boolean {
    return _.isUserActive() && !_.isNothing(_.getSession());
  }
});

_.mixin({
  refreshMenu(): void {
    const val = _.get($store, 'App.menuRefresh$.value',0) + 1;
    _.get($store, 'App.menuRefresh$').next(val);
  }
});

_.mixin({
  preventPaste(el: any): void {
    if (_.isEmpty(el)) {
      return;
    }
    if (_.get(el, 'length', 0)) {
      _.each(el, (o: any) => (o.onpaste = (e: any) => e.preventDefault()));
    } else {
      el.onpaste = (e: any) => e.preventDefault();
    }
  }
});

_.mixin({
  qualityFilter(url: any): string {
    if (_.isString(url)) {
      if (!_.includes(url, Strings.cloudinaryMediaQuality)) {
        url = (!_.isAudio(url) ? _.replaceAll(url, Strings.cloudinaryMediaUpload, Strings.cloudinaryMediaQuality) : url);
      }
    } else {
      if (_.has(url, 'cdn')) {
        if (!_.includes(url.cdn, Strings.cloudinaryMediaQuality)) {
          url.cdn = (!_.isAudio(url) ? _.replaceAll(url.cdn, Strings.cloudinaryMediaUpload, Strings.cloudinaryMediaQuality) : url.cdn);
        }
      }
      if (_.has(url, 'thumbnail.cdn')) {
        if (!_.includes(url.thumbnail.cdn, Strings.cloudinaryMediaQuality)) {
          url.thumbnail.cdn = (!_.isAudio(url) ? _.replaceAll(url.thumbnail.cdn, Strings.cloudinaryMediaUpload, Strings.cloudinaryMediaQuality) : url.thumbnail.cdn);
        }
      }
    }
    return url;
  }
});

_.mixin({
  supportsConic(): boolean {
    return CSS.supports('background-image', 'conic-gradient(from 180deg at 50% 50%, red, yellow, green, cyan, blue, magenta, red)') ||
      CSS.supports('background-image', '-webkit-conic-gradient(from 180deg at 50% 50%, red, yellow, green, cyan, blue, magenta, red)');
  }
});

_.mixin({
  toastError(message: string, options?: ToastOptions, debounce = true): void {
    const id: string = _.get($store, 'App.activeDialogId');
    if (debounce) {
      debouncedToastError(message, options, id);
    } else {
      toast.error(message, {
        ...options,
        containerId: id ?? undefined,
        icon: options?.icon ?? ToastIcons.error,
        style: {
          "background": "#660a0a",
          "color": "#ffc9c9"
        }
      });
    }
  }
});

_.mixin({
  toastInfo(message: string, options?: ToastOptions, debounce = true): void {
    const id = _.get($store, 'App.activeDialogId');
    if (debounce) {
      debouncedToastInfo(message, options, id);
    } else {
      toast.info(message, {
        ...options,
        containerId: id ?? undefined,
        icon: options?.icon ?? ToastIcons.info,
        style: {
          "background": "181818F2",
          "color": "#ffffff"
        }
      });
    }
  }
});

_.mixin({
  toastSuccess(message: string, options?: ToastOptions): void {
    const id = _.get($store, 'App.activeDialogId');
    toast.success(message, {
      ...options,
      containerId: id ?? undefined,
      icon: options?.icon ?? ToastIcons.success,
      style: {
        "background": "#030b12b3",
        "color": "#819fb6"
      }
    });
  }
});

_.mixin({
  forgetAccount(): void {
    _.set($store, 'User.user', null);
    _.get($store, 'User.user$').next(null);
    _.set($store, 'User.session', null);
    _.get($store, 'User.session$').next(null);
    _.get($store, 'User.activationPending$').next(false);
    _.get($store, 'User.signedIn$').next(false);
  }
});

_.mixin({
  async toBase64(input: string): Promise<string> {
    if (input.startsWith("data:image")) return input;

    const res = await fetch(input);
    const blob = await res.blob();

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
});

_.mixin({
  windowResize(): void {
    if (typeof window === 'undefined') return;

    const computeState = (e?: Event | CustomEvent) => {
      const pw = window.innerWidth;
      const ph = window.innerHeight;

      const o = ph > pw ? 'portrait' : 'landscape';

      const size =
        pw < 431 ? 'size-xxs' :
          pw < 576 ? 'size-xs' :
            pw < 768 ? 'size-sm' :
              pw < 992 ? (ph > pw / 2 ? 'size-md' : 'size-sm') :
                pw < 1200 ? 'size-lg' :
                  pw < 1400 ? 'size-xl' : 'size-xxl';

      const pgh = ph - 99;

      const windowState = {
        size,
        orientation: o,
        width: pw,
        height: ph,
        pageHeight: pgh
      };

      const state$: any = _.get($store, 'App.state$');

      if (
        state$ &&
        (
          !_.isEqual(state$.value?.window, windowState) ||
          (e as any)?.detail === 'first'
        )
      ) {
        state$.next(_.merge({}, state$.value, { window: windowState }));

        // ✅ CSS variables (sync immediately)
        const root = document.documentElement;
        root.style.setProperty('--size', size);
        root.style.setProperty('--orientation', o);
        root.style.setProperty('--height', `${ph}px`);
        root.style.setProperty('--page-height', `${pgh}px`);

        // ✅ custom event bus
        const $e = (window as any).$e;
        if ($e?.$b && $e?.events?.windowResize) {
          $e.$b($e.events.windowResize, state$.value, false);
        }
      }
    };

    // 🔥 Debounced version ONLY for resize events
    const debouncedResize = _.debounce(computeState, 200, {
      leading: false,
      trailing: true
    });

    // ✅ Attach listener
    window.addEventListener('resize', debouncedResize);

    // 🔥 CRITICAL: run immediately (NO debounce)
    computeState({ detail: 'first' } as any);
  }
});

// _.mixin({
//   windowResize(): void {
//     if (typeof window === 'undefined') return;
//
//     const resizeHandler = _.debounce((e: Event | CustomEvent) => {
//       const pw = window.innerWidth;
//       const ph = window.innerHeight;
//
//       const o = ph > pw ? 'portrait' : 'landscape';
//       const size =
//         pw < 431 ? 'size-xxs' :
//           pw < 576 ? 'size-xs' :
//             pw < 768 ? 'size-sm' :
//               pw < 992 ? (ph > pw / 2 ? 'size-md' : 'size-sm') :
//                 pw < 1200 ? 'size-lg' :
//                   pw < 1400 ? 'size-xl' : 'size-xxl';
//
//       const pgh = ph - 99;
//       const windowState = {
//         size,
//         orientation: o,
//         width: pw,
//         height: ph,
//         pageHeight: pgh
//       };
//
//       const state$: any = _.get($store, 'App.state$');
//       if (state$ && (!_.isEqual(state$.value?.window, windowState) || (e as any)?.detail === 'first')) {
//         state$.next(_.merge({}, state$.value, {window: windowState}));
//
//         const root = document.documentElement;
//         root.style.setProperty('--size', size);
//         root.style.setProperty('--orientation', o);
//         root.style.setProperty('--height', `${ph}px`);
//         root.style.setProperty('--page-height', `${pgh}px`);
//
//         const $e = (window as any).$e;
//         if ($e?.$b && $e?.events?.windowResize) {
//           $e.$b($e.events.windowResize, state$.value, false);
//         }
//       }
//     }, 200, {
//       leading: false,
//       trailing: true
//     });
//
//     window.addEventListener('resize', resizeHandler);
//     window.dispatchEvent(new CustomEvent('resize', {detail: 'first'}));
//   }
// });

export default _;